import { Autocomplete, Box, MenuItem } from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMainComponentCache, useBulkSendToServiceStation, useBulkSendToStorage } from 'src/api/main-component'
import {
  invalidateComponentGetByStatus,
  invalidateGetServiceStationById,
  invalidateServiceStationCache,
  useGetAllEligibleServiceStations,
} from 'src/api/service-station'
import { invalidateStorageCache, useGetAllStorageWithCustomers } from 'src/api/storage'
import { AppTextField } from 'src/components/AppTextField'
import { CancelButton } from 'src/components/CancelButton'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { FlexSpinner } from 'src/components/Spinner'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'
import { FacilityTypes, MainComponentStatus, MovementType } from 'src/enums'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { IServiceStation } from 'src/generated-types/service-station'
import { IStorage } from 'src/generated-types/storage-types'
import { useZodForm } from 'src/hooks/zod-form'
import { GroupHeader, GroupItems } from 'src/screens/localities/positions/components/MainComponentSendModal'
import { ShowDialogProps } from 'src/show-dialog'
import { Padding } from 'src/styles/paddingComponent'
import { z } from 'zod'

const storageSchema = z.object({
  storageId: z.number(),
  hasPendingService: z.boolean().optional(),
})

const serviceStationSchema = z.object({
  serviceStationId: z.number(),
})

export interface BulkSendToDialogProps extends ShowDialogProps {
  customerId: EntityId
  selectedEquipments: IMainComponent[]
  onSuccess?: () => void
}

export const BulkSendToDialog: FC<BulkSendToDialogProps> = ({ customerId, selectedEquipments, pop, onSuccess }) => {
  const { t } = useTranslation(['main-component', 'common', 'position', 'page-container'])
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)
  const [facilityType, setFacilityType] = useState(FacilityTypes.serviceStation)

  const equipmentTypeIds = useMemo(
    () => Array.from(new Set(selectedEquipments.map(e => e.type?.id).filter(Boolean) as number[])),
    [selectedEquipments],
  )

  const { data: sendToServiceStationSchemas, isLoading: isLoadingSendToServiceStationSchemas } =
    useGetFormSchemasForMovement(customerId, {
      equipmentTypeIds,
      movementTypes: [MovementType.SendToServiceStation],
    })

  const { data: sendToStorageSchemas, isLoading: isLoadingSendToStorageSchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds, movementTypes: [MovementType.SendToStorage] },
  )

  const { mutateAsync: bulkSendToStorage } = useBulkSendToStorage(customerId as EntityId)
  const { mutateAsync: bulkSendToServiceStation } = useBulkSendToServiceStation(customerId as EntityId)
  const { data: storages, isLoading: storagesLoading } = useGetAllStorageWithCustomers(customerId)
  const { data: serviceStations, isLoading: serviceStationsLoading } = useGetAllEligibleServiceStations(
    customerId as EntityId,
  )

  const storageForm = useZodForm(storageSchema)
  const serviceStationForm = useZodForm(serviceStationSchema)

  const items = selectedEquipments
    .filter(e => e.id != null)
    .map(e => ({ id: e.id as number, localityId: e.selectedLocalityId as number }))

  const invalidate = useCallback(
    (storageId?: number, serviceStationId?: number) => {
      invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
      selectedEquipments.forEach(e => {
        invalidateMainComponentCache.getMainComponentById(customerId as EntityId, e.id as EntityId)
        if (e.selectedLocalityId) {
          invalidateLocalityCache.getLocality(customerId as EntityId, e.selectedLocalityId as EntityId)
        }
      })
      invalidateCustomer.getCustomersActivityCounts(customerId)
      invalidateCertificateCache.getCertificates(0)
      if (storageId) {
        invalidateStorageCache.getEquipmentInStorage(customerId as EntityId, storageId as EntityId)
      }
      if (serviceStationId) {
        invalidateGetServiceStationById(customerId, serviceStationId as EntityId)
        invalidateServiceStationCache.getServiceStations(customerId)
        invalidateComponentGetByStatus(customerId, serviceStationId as EntityId, MainComponentStatus.Incoming)
      }
      invalidateDeviation.getDeviations(customerId)
    },
    [customerId, selectedEquipments],
  )

  const performBulkSendToStorage = async (
    formValues: { storageId: number; hasPendingService?: boolean },
    equipmentReportsMap: Map<number, IFormSchemaReport[]>,
  ) => {
    const itemsWithReports = items.map(item => ({
      ...item,
      formSchemaReports: equipmentReportsMap.get(item.id) ?? [],
    }))

    await bulkSendToStorage(
      {
        items: itemsWithReports,
        storageId: formValues.storageId,
        hasPendingService: formValues.hasPendingService ?? undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('sendToStorageSuccess', { ns: 'main-component' }))
          invalidate(formValues.storageId)
          pop()
          onSuccess?.()
        },
      },
    )
  }

  const performBulkSendToServiceStation = async (
    formValues: { serviceStationId: number },
    equipmentReportsMap: Map<number, IFormSchemaReport[]>,
  ) => {
    const itemsWithReports = items.map(item => ({
      ...item,
      formSchemaReports: equipmentReportsMap.get(item.id) ?? [],
    }))

    await bulkSendToServiceStation(
      {
        items: itemsWithReports,
        serviceStationId: formValues.serviceStationId,
      },
      {
        onSuccess: () => {
          toast.success(t('sendToServiceStationSuccess', { ns: 'main-component' }))
          invalidate(undefined, formValues.serviceStationId)
          pop()
          onSuccess?.()
        },
      },
    )
  }

  const onSubmitStorage = useCallback(
    async (formValues: { storageId: number; hasPendingService?: boolean }) => {
      if (items.length === 0) return

      await showChecklistsDialog({
        isBulk: true,
        schemas: sendToStorageSchemas,
        isLoadingSchemas: isLoadingSendToStorageSchemas,
        selectedEquipments,
        onConfirm: reportsMap => performBulkSendToStorage(formValues, reportsMap),
      })
    },
    [items, showChecklistsDialog, sendToStorageSchemas, customerId, selectedEquipments],
  )

  const onSubmitServiceStation = useCallback(
    async (formValues: { serviceStationId: number }) => {
      if (items.length === 0) return

      await showChecklistsDialog({
        isBulk: true,
        schemas: sendToServiceStationSchemas,
        isLoadingSchemas: isLoadingSendToServiceStationSchemas,
        selectedEquipments,
        onConfirm: reportsMap => performBulkSendToServiceStation(formValues, reportsMap),
      })
    },
    [items, showChecklistsDialog, sendToServiceStationSchemas, customerId, selectedEquipments],
  )

  const isLoading = storagesLoading || serviceStationsLoading
  if (isLoading) return <FlexSpinner />

  return (
    <GenericDialogLayout
      sx={{ paddingX: '20px' }}
      title={t('sendTo', { ns: 'main-component' })}
      pop={pop}
      titleSectionBottomPadding="10px"
      titleSectionTopPadding="20px"
      titleSectionXPadding="20px"
    >
      <Box width="100%" paddingLeft="10px">
        <Padding.pb8>
          <ToggleGroupButtons
            actions={[
              { title: t('service-station', { ns: 'page-container' }), value: FacilityTypes.serviceStation },
              { title: t('storage', { ns: 'page-container' }), value: FacilityTypes.storage },
            ]}
            defaultValue={FacilityTypes.serviceStation}
            value={facilityType}
            setViewMode={value => setFacilityType(value)}
            buttonWidth="180px"
          />
        </Padding.pb8>
      </Box>

      {facilityType === FacilityTypes.storage ? (
        <AppForm form={storageForm} onSubmit={onSubmitStorage}>
          <Autocomplete
            onChange={(_, value) => storageForm.setValue('storageId', (value as IStorage).id)}
            options={storages ?? []}
            sx={{ paddingX: '0px', '& .MuiBox-root': { paddingX: '0px' } }}
            groupBy={(option: IStorage & { customer?: { name?: string; organizationNr?: string } }) =>
              `${option.customer?.name ?? ''} ${
                option.customer?.organizationNr ? `(${option.customer.organizationNr})` : ''
              }`
            }
            renderGroup={params => (
              <li>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            autoHighlight
            getOptionLabel={o => o.name}
            renderOption={(props, o) => (
              <MenuItem {...props} key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            )}
            renderInput={params => (
              <AppTextField
                {...params}
                label={t('select-storage', { ns: 'main-component' })}
                inputProps={{ ...params.inputProps }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Box className="flex justify-end gap-4 pt-4">
            <FormSubmitButton createText={t('Send', { ns: 'common' })} />
            <CancelButton onClick={() => pop()}>{t('cancel', { ns: 'common' })}</CancelButton>
          </Box>
        </AppForm>
      ) : (
        <AppForm form={serviceStationForm} onSubmit={onSubmitServiceStation}>
          <Box pb={2}>
            {t('sendingEquipmentCount', {
              count: items.length,
              ns: 'main-component',
              defaultValue: `Sending ${items.length} equipment`,
            })}
          </Box>
          <Autocomplete
            onChange={(_, value) => {
              serviceStationForm.setValue('serviceStationId', (value as IServiceStation).id)
            }}
            options={(serviceStations ?? []).sort((a, b) =>
              (b.type?.toString() ?? '').localeCompare(a.type?.toString() ?? ''),
            )}
            groupBy={(option: IServiceStation & { customer?: { name?: string; organizationNr?: string } }) =>
              `${option.customer?.name ?? ''} ${
                option.customer?.organizationNr ? `(${option.customer.organizationNr})` : ''
              }`
            }
            renderGroup={params => (
              <li>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            autoHighlight
            getOptionLabel={o => o.name}
            renderOption={(props, o) => (
              <MenuItem {...props} key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            )}
            renderInput={params => (
              <AppTextField
                {...params}
                label={t('select-service-station', { ns: 'main-component' })}
                inputProps={{ ...params.inputProps }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Box className="flex justify-end gap-4 pt-4">
            <FormSubmitButton createText={t('Send', { ns: 'common' })} />
            <CancelButton onClick={() => pop()}>{t('cancel', { ns: 'common' })}</CancelButton>
          </Box>
        </AppForm>
      )}
    </GenericDialogLayout>
  )
}
